import React, { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import * as UIcons from '@iconscout/react-unicons'
import Btn from 'components/Btn'

const QUICK_AMOUNTS = [1000, 3000, 5000, 10000, 30000]
const MINIMUM_TOP_UP = 100
const CURRENT_BALANCE = 18450.60

const formatMoney = (value) => `GHS ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

function ModalShell({ title, progress, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[5000] bg-black bg-opacity-30 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white relative w-11/12 max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="relative flex items-center justify-center px-6 py-5">
          <button type="button" onClick={onClose} className="absolute left-6 text-secondary hover:text-dark-blue">
            <UIcons.UilTimes size={22} />
          </button>

          <h3 className="text-xl font-bold text-dark-blue">{title}</h3>
        </div>

        {
          progress != null && (
            <div className="h-1.5 bg-border-lines">
              <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
            </div>
          )
        }

        {children}
      </div>
    </div>
  )
}

export default withTranslation()(function TopUpCreditModal({ t, handleClick }) {
  const [step, setStep] = useState('detail')
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState(null)
  const [reference] = useState(() => `RA-TOP-${Math.floor(100000 + Math.random() * 900000)}`)
  const [submittedAt] = useState(() => dayjs())

  const numericAmount = Number(amount) || 0
  const detailValid = numericAmount >= MINIMUM_TOP_UP && Boolean(method)
  const processingFee = 0
  const totalToPay = numericAmount + processingFee
  const expectedBalance = CURRENT_BALANCE + numericAmount

  useEffect(() => {
    if (step !== 'processing') return undefined

    // Mock payment gateway: no real provider is wired up yet, so this
    // simulates an outcome instead of always succeeding, so both the
    // success and failure screens are reachable for review.
    const timeout = setTimeout(() => setStep(Math.random() < 0.8 ? 'success' : 'failure'), 1800)
    return () => clearTimeout(timeout)
  }, [step])

  const downloadReceipt = () => toast.info(t('billing.top_up.success.receipt_not_available'))

  if (step === 'detail') {
    return (
      <ModalShell title={t('billing.top_up.title')} progress={20} onClose={handleClick}>
        <div className="flex items-start gap-3 bg-primary bg-opacity-5 text-primary text-sm px-6 py-3 mx-6 mt-4 rounded-lg">
          <UIcons.UilInfoCircle size={18} className="shrink-0 mt-0.5" />
          <p>{t('billing.top_up.banner')}</p>
        </div>

        <div className="px-6 py-5">
          <p className="text-xs font-semibold text-secondary uppercase mb-3">{t('billing.top_up.detail.section_title')}</p>

          <div className="bg-background rounded-lg p-4 mb-5">
            <p className="text-xs font-semibold text-secondary uppercase mb-1">{t('billing.top_up.detail.available_balance')}</p>
            <p className="text-base text-dark-blue">{formatMoney(CURRENT_BALANCE)}</p>
          </div>

          <label className="text-sm text-dark-blue inline-block mb-2">{t('billing.top_up.detail.amount_label')}</label>
          <div className="flex items-center gap-2 border border-border-lines rounded-xl px-4 py-3 mb-1.5">
            <span className="text-dark-blue font-semibold">GHS</span>
            <input
              type="number"
              min={0}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="0.00"
              className="flex-1 outline-none text-dark-blue"
            />
          </div>
          <p className="text-xs text-secondary mb-4">{t('billing.top_up.detail.minimum', { amount: MINIMUM_TOP_UP })}</p>

          <div className="flex flex-wrap gap-2 mb-6">
            {
              QUICK_AMOUNTS.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setAmount(String(value))}
                  className={`px-3 py-1.5 rounded-full border text-sm font-medium whitespace-nowrap ${
                    numericAmount === value ? 'border-primary text-primary bg-primary bg-opacity-5' : 'border-border-lines text-dark-blue'
                  }`}
                >
                  {formatMoney(value).replace('.00', '')}
                </button>
              ))
            }
          </div>

          <p className="text-sm text-dark-blue mb-2">{t('billing.top_up.detail.methods_title')}</p>

          <div className="border border-border-lines rounded-xl divide-y divide-border-lines mb-6">
            {
              [
                { key: 'mobile_money', label: t('billing.top_up.detail.mobile_money'), desc: t('billing.top_up.detail.mobile_money_desc') },
                { key: 'card', label: t('billing.top_up.detail.card'), desc: t('billing.top_up.detail.card_desc') }
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setMethod(option.key)}
                  className="w-full flex items-center gap-3 px-4 py-4 text-left"
                >
                  <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${method === option.key ? 'border-primary' : 'border-border-lines'}`}>
                    {method === option.key && <span className="w-2.5 h-2.5 rounded-full bg-primary" />}
                  </span>

                  <span>
                    <span className="block text-base font-semibold text-dark-blue">{option.label}</span>
                    <span className="block text-sm text-secondary">{option.desc}</span>
                  </span>
                </button>
              ))
            }
          </div>

          <Btn
            content={() => <span className="flex items-center justify-center gap-2">{t('billing.top_up.detail.continue')} <UIcons.UilArrowRight size={16} /></span>}
            className="w-full h-12 rounded-full bg-primary text-white disabled:opacity-40"
            click={() => setStep('summary')}
            disabled={!detailValid}
          />
        </div>
      </ModalShell>
    )
  }

  if (step === 'summary') {
    return (
      <ModalShell title={t('billing.top_up.title')} progress={60} onClose={handleClick}>
        <div className="flex items-start gap-3 bg-primary bg-opacity-5 text-primary text-sm px-6 py-3 mx-6 mt-4 rounded-lg">
          <UIcons.UilInfoCircle size={18} className="shrink-0 mt-0.5" />
          <p>{t('billing.top_up.banner')}</p>
        </div>

        <div className="px-6 py-5">
          <p className="text-xs font-semibold text-secondary uppercase mb-3">{t('billing.top_up.summary.section_title')}</p>

          <div className="border border-border-lines rounded-xl divide-y divide-border-lines mb-4">
            {
              [
                { label: t('billing.top_up.summary.current_balance'), value: formatMoney(CURRENT_BALANCE) },
                { label: t('billing.top_up.summary.top_up_amount'), value: formatMoney(numericAmount) },
                { label: t('billing.top_up.summary.processing_fee'), value: formatMoney(processingFee) },
                { label: t('billing.top_up.summary.total_to_pay'), value: formatMoney(totalToPay), bold: true },
                { label: t('billing.top_up.summary.expected_balance'), value: formatMoney(expectedBalance), bold: true }
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-4 px-4 py-3">
                  <p className="text-secondary">{row.label}</p>
                  <p className={row.bold ? 'font-semibold text-dark-blue' : 'text-dark-blue'}>{row.value}</p>
                </div>
              ))
            }
          </div>

          <div className="border border-border-lines rounded-xl divide-y divide-border-lines mb-6">
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <p className="text-secondary">{t('billing.top_up.summary.payment_method')}</p>
              <p className="text-dark-blue">{method === 'card' ? t('billing.top_up.detail.card') : t('billing.top_up.detail.mobile_money')}</p>
            </div>
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <p className="text-secondary">{t('billing.top_up.summary.account')}</p>
              <p className="text-dark-blue">{t('billing.top_up.summary.credit_account')}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Btn
              content={() => <span className="flex items-center justify-center gap-2"><UIcons.UilArrowLeft size={16} /> {t('billing.top_up.summary.back')}</span>}
              className="flex-1 h-12 rounded-full border border-border-lines text-dark-blue"
              click={() => setStep('detail')}
            />

            <Btn
              content={() => <span className="flex items-center justify-center gap-2">{t('billing.top_up.summary.continue_to_pay')} <UIcons.UilArrowRight size={16} /></span>}
              className="flex-1 h-12 rounded-full bg-primary text-white"
              click={() => setStep('processing')}
            />
          </div>
        </div>
      </ModalShell>
    )
  }

  if (step === 'processing') {
    return (
      <ModalShell title={t('billing.top_up.title')} progress={80} onClose={handleClick}>
        <div className="px-6 py-16 flex flex-col items-center text-center gap-4">
          <span className="w-10 h-10 border-4 border-border-lines border-t-primary rounded-full animate-spin" />
          <p className="text-base font-semibold text-dark-blue">{t('billing.top_up.processing.title')}</p>
          <p className="text-sm text-secondary max-w-xs">{t('billing.top_up.processing.hint')}</p>
        </div>
      </ModalShell>
    )
  }

  if (step === 'success') {
    return (
      <div className="fixed inset-0 z-[5000] bg-black bg-opacity-30 flex items-center justify-center p-4">
        <div className="bg-white relative w-11/12 max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl p-8 text-center">
          <span className="inline-flex w-16 h-16 rounded-full bg-success items-center justify-center mb-4">
            <UIcons.UilCheck size={32} className="text-white" />
          </span>

          <h3 className="text-xl font-bold text-dark-blue mb-1">{t('billing.top_up.success.title')}</h3>
          <p className="text-sm text-secondary max-w-sm mx-auto mb-6">{t('billing.top_up.success.description')}</p>

          <div className="border border-border-lines rounded-xl divide-y divide-border-lines mb-4 text-left">
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <p className="text-secondary">{t('billing.top_up.success.amount_credited')}</p>
              <p className="font-semibold text-dark-blue">{formatMoney(numericAmount)}</p>
            </div>
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <p className="text-secondary">{t('billing.top_up.success.new_balance')}</p>
              <p className="font-semibold text-dark-blue">{formatMoney(expectedBalance)}</p>
            </div>
          </div>

          <div className="border border-border-lines rounded-xl divide-y divide-border-lines mb-6 text-left">
            <p className="px-4 pt-3 text-xs font-semibold text-secondary uppercase">{t('billing.top_up.success.section_title')}</p>
            {
              [
                { label: t('billing.top_up.success.status'), value: t('billing.top_up.success.successful'), valueClassName: 'text-success font-semibold' },
                { label: t('billing.top_up.summary.account'), value: t('billing.top_up.summary.credit_account') },
                { label: t('billing.top_up.success.payment_method'), value: method === 'card' ? `${t('billing.top_up.detail.card')} (VISA)` : 'MTN Mobile Money' },
                method === 'card'
                  ? { label: t('billing.top_up.success.card'), value: '•••• 4242' }
                  : { label: t('billing.top_up.success.mobile_number'), value: '+233 55 123 4567' },
                { label: t('billing.top_up.success.payment_reference'), value: reference },
                { label: t('billing.top_up.success.date_time'), value: submittedAt.format('DD MMM YYYY, h:mm A') },
                { label: t('billing.top_up.success.processing_fee'), value: formatMoney(processingFee) },
                { label: t('billing.top_up.success.total_paid'), value: formatMoney(totalToPay) }
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-4 px-4 py-3">
                  <p className="text-secondary">{row.label}</p>
                  <p className={row.valueClassName ?? 'text-dark-blue'}>{row.value}</p>
                </div>
              ))
            }
          </div>

          <Btn
            content={t('billing.top_up.success.got_it')}
            className="w-full h-12 rounded-full bg-primary text-white mb-3"
            click={handleClick}
          />

          <button type="button" onClick={downloadReceipt} className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold">
            <UIcons.UilImport size={16} /> {t('billing.top_up.success.download_receipt')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[5000] bg-black bg-opacity-30 flex items-center justify-center p-4">
      <div className="bg-white relative w-11/12 max-w-md max-h-[90vh] overflow-y-auto rounded-2xl p-8 text-center">
        <span className="inline-flex w-16 h-16 rounded-full bg-danger items-center justify-center mb-4">
          <UIcons.UilTimes size={32} className="text-white" />
        </span>

        <h3 className="text-xl font-bold text-dark-blue mb-1">{t('billing.top_up.failure.title')}</h3>
        <p className="text-sm text-secondary max-w-xs mx-auto mb-6">{t('billing.top_up.failure.description')}</p>

        <Btn
          content={() => <span className="flex items-center justify-center gap-2"><UIcons.UilRedo size={16} /> {t('billing.top_up.failure.try_again')}</span>}
          className="w-full h-12 rounded-full bg-primary text-white mb-3"
          click={() => setStep('processing')}
        />

        <button type="button" onClick={handleClick} className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold">
          <UIcons.UilTimes size={16} /> {t('billing.top_up.failure.cancel')}
        </button>
      </div>
    </div>
  )
})
